import { useEffect, useState, type ReactNode } from 'react';
import { enablePush, pushState, type PushState } from '../../mail/push';
import {
	canInstall,
	isIOS,
	isStandalone,
	onInstallChange,
	promptInstall,
	unblockInstructions
} from '../../mail/device';
import { Button } from '../../ui';

const DISMISS_KEY = 'ks-notify-prompt-dismissed';
// "Not now" is a week, not forever: forever is how a setting gets lost.
const DISMISS_FOR = 7 * 24 * 60 * 60 * 1000;

function dismissedRecently(): boolean {
	try {
		const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
		return Date.now() - at < DISMISS_FOR;
	} catch {
		return false;
	}
}

/**
 * A banner at the top of the mailbox that gets notifications turned on in one
 * tap.
 *
 * Before this, the only way in was Settings › Notifications, which most people
 * never open -- and on a phone that is the whole point of the app. It also says
 * what to do in the cases a switch cannot fix: an iPhone that has not added the
 * app to its Home Screen, and a browser where notifications were blocked.
 */
export function NotificationPrompt({ pushKey, workerUrl }: { pushKey?: string; workerUrl?: string }) {
	const [state, setState] = useState<PushState | null>(null);
	const [hidden, setHidden] = useState(dismissedRecently);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const [installable, setInstallable] = useState(canInstall);

	useEffect(() => {
		pushState()
			.then(setState)
			.catch(() => setState('unsupported'));
		return onInstallChange(() => setInstallable(canInstall()));
	}, []);

	const configured = Boolean(pushKey && workerUrl);
	// iPhone Safari has no Web Push at all until the app is on the Home Screen, so
	// "unsupported" there really means "not installed yet".
	const needsHomeScreen = isIOS && !isStandalone();

	if (!configured || hidden || null === state || 'on' === state) {
		return null;
	}
	if ('unsupported' === state && !needsHomeScreen) {
		return null;
	}

	function dismiss() {
		try {
			localStorage.setItem(DISMISS_KEY, String(Date.now()));
		} catch {
			// Private mode: the banner simply comes back next time.
		}
		setHidden(true);
	}

	async function turnOn() {
		if (!pushKey || !workerUrl) {
			return;
		}
		setBusy(true);
		setError('');
		try {
			setState(await enablePush(pushKey, workerUrl));
		} catch (e: unknown) {
			setError(e instanceof Error ? e.message : 'Could not turn on notifications.');
		} finally {
			setBusy(false);
		}
	}

	let text: string;
	let action: ReactNode = null;

	if (needsHomeScreen) {
		text =
			'To get new-mail notifications on iPhone, tap the Share button, choose “Add to Home Screen”, then open Keystone Mail from your Home Screen.';
	} else if ('blocked' === state) {
		text = unblockInstructions();
	} else {
		text = 'Get a notification on this device when new mail arrives — even with the app closed.';
		action = (
			<Button size="sm" onClick={() => void turnOn()} loading={busy}>
				Turn on
			</Button>
		);
	}

	return (
		<div className="notice" role="status">
			<p className="notice__text">{text}</p>
			{error && <p className="notice__text ks-error">{error}</p>}
			<div className="notice__actions">
				{action}
				{installable && !needsHomeScreen && (
					<Button size="sm" variant="outline" onClick={() => void promptInstall()}>
						Install app
					</Button>
				)}
				<Button size="sm" variant="ghost" onClick={dismiss}>
					Not now
				</Button>
			</div>
		</div>
	);
}
